import { Component, OnDestroy, HostListener, Input, AfterViewInit, ViewChildren, Host } from '@angular/core';
import * as Chart from 'chart.js';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { getQueryParamFromAncestorRoute, getParamFromAncestorRoute } from '../../../utilities/route.utilities';
import { ConsumptionService } from '../../../services/consumption.service';
import { ReadDataAvailabilityGridItem } from '../grid-item/read-data-availability-grid-item.component';

@Component({
    selector: 'read-data-availability-consumption-chart',
    template: '<canvas style="height:35rem;" id="basechart" #basechart></canvas>',
    styles: [':host { position: relative; display:block;}']
})
export class ReadDataAvailabilityConsumptionChartComponent implements AfterViewInit, OnDestroy {

    @ViewChildren('basechart') basechart: any;
    chart: any;
    ctx: any;
    public subscriptions: any[] = [];
    debounceTimeout = 2000;
    debounceId: any;
    chartData: any;
    supplyTypesData: any[] = [];
    filterList: any[] = [];
    filter: any[] = [];
    heatGradientStroke: any;
    hotWaterGradientStroke: any;
    coldWaterGradientStroke: any;
    supplyType: string;
    pipe: DatePipe = new DatePipe('en_GB');
    clientId: number;
    timePeriod: number = 12;
    siteId: number = 0;
    averageConsumption: any;
    initalLoad: boolean = false;
    minTime: string = '';
    maxTime: string = '';
    hasCurrent: boolean = false;
    has_180365Days: boolean = false;
    has_365Days: boolean = false;
    has_6090Days: boolean = false;
    has_90180Days: boolean = false;
    hasBankTransfer: boolean = false;

    constructor(
        private consumptionService: ConsumptionService,
        private route: ActivatedRoute,
        private router: Router,
        @Host() private parent: ReadDataAvailabilityGridItem
    ) { }

    ngAfterViewInit(): void {
        this.route.queryParams.subscribe(queryParams => {
            this.onInitialise();
        });
    }

    onInitialise() {

        this.clientId = getParamFromAncestorRoute(this.route, 'clientId');
        this.siteId = getQueryParamFromAncestorRoute(this.route, 'siteId') ? getQueryParamFromAncestorRoute(this.route, 'siteId') : 0;
        this.timePeriod = getQueryParamFromAncestorRoute(this.route, 'timePeriod') == null ? 12 : getQueryParamFromAncestorRoute(this.route, 'timePeriod');
        this.supplyType = getQueryParamFromAncestorRoute(this.route, 'supplyCategory') == null ? "Heat" : getQueryParamFromAncestorRoute(this.route, 'supplyCategory');

        this.search();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
        if (this.debounceId) {
            clearTimeout(this.debounceId);
        }
    }

    unique = (value, index, self) => {
        return self.indexOf(value) === index;
    }

    firstLetterToLower(item: string) {
        return item.charAt(0).toLowerCase() + item.slice(1);
    }


    @HostListener('window:resize', ['$event'])
    onResize(event: any) {
        this.createChart();
    }

    search() {

        this.chartData = [];
        if (this.timePeriod == 12) {
            //monthly
            this.consumptionService.getMonthlyReadDataAvailability(this.clientId, this.supplyType, this.siteId).then((result) => {
                this.averageConsumption = result;
                this.chartData = this.processData(this.averageConsumption);
                this.parent.hasData = true;
                this.parent.hasLoading = false;
                this.createChart();
            }).catch(reason => {
                this.parent.hasData = false;
                this.parent.hasLoading = false;
            });
        }

        if (this.timePeriod == 90) {
            // daily
            this.consumptionService.getDailyReadDataAvailability(this.clientId, this.supplyType, this.siteId).then((result) => {
                this.averageConsumption = result;
                this.chartData = this.processData(this.averageConsumption);
                this.parent.hasData = true;
                this.parent.hasLoading = false;
                this.createChart();
            }).catch(reason => {
                this.parent.hasData = false;
                this.parent.hasLoading = false;
            });
        }
    }

    processData(data: any[]) {

        if (!data || data.length == 0) return;
        let monthCount: number = 0;
        this.hasCurrent = false;
        this.has_180365Days = false;
        this.has_365Days = false;
        this.has_6090Days = false;
        this.has_90180Days = false;

        let graphData = {
            labels: [],
            availablePercentage: [],
            goodAvailabilityLimitPercentage: 0,
            poorAvailabilityLimitPercentage: 0
        };

        let dateMax = this.timePeriod == 12 ? new Date(data[data.length - 1].monthYearDate) : new Date(data[data.length - 1].date);
        let dateMin = this.timePeriod == 12 ? new Date(data[0].monthYearDate) : new Date(data[0].date);
        if (this.timePeriod == 90) {
            dateMax.setDate(dateMax.getUTCDate() + 5);
            dateMin.setDate(dateMin.getUTCDate() - 5);
        } else {
            dateMax.setDate(dateMax.getUTCMonth() + 2);
            dateMin.setDate(dateMin.getUTCMonth() - 1);
        }
        this.maxTime = dateMax.toISOString();
        this.minTime = dateMin.toISOString();
        graphData.availablePercentage = data.map(item => {
            if (this.timePeriod == 12) { item.monthYearDate = item.monthYearDate.split('T')[0]; }
            if (this.timePeriod == 90) { item.date = item.date.split('T')[0]; }
                
            return {
                x: this.timePeriod == 12 ?  item.monthYear : item.date,
                y: item.availablePercentage
            }
        });

        graphData.goodAvailabilityLimitPercentage = data[0].goodAvailabilityLimitPercentage;

        graphData.poorAvailabilityLimitPercentage = data[0].poorAvailabilityLimitPercentage;

        return (graphData);
    }

    private getChartConfig(): any {
        let config = {
            type: 'line',
            options: {
                annotation: {
                    annotations: [{
                        drawTime: 'afterDraw',
                        type: 'line',
                        scaleID: "y-axis-0",
                        mode: 'horizontal',
                        value: this.chartData.goodAvailabilityLimitPercentage,
                        borderColor: '#93c01f',
                        borderWidth: 2.5,
                        borderDash: [10, 6],
                    },
                    {
                        drawTime: 'afterDraw',
                        type: 'line',
                        scaleID: "y-axis-0",
                        mode: 'horizontal',
                        value: this.chartData.poorAvailabilityLimitPercentage,
                        borderColor: '#C82B2B',
                        borderWidth: 2.5,
                        borderDash: [10, 6],
                    }
                    ]
                },
                tooltips: {
                    mode: 'index',
                    enabled: true,
                    backgroundColor: '#ffffff',
                    titleFontColor: '#153954',
                    bodyFontColor: '#153954',
                    borderWidth: 2,
                    borderColor: '#009aa3',
                    callbacks: {
                        label: function (tooltipItem, data) {
                            var allData: any = data != null && data.datasets != null && tooltipItem != null && tooltipItem.datasetIndex != null ? data.datasets[tooltipItem.datasetIndex].data : [];
                            var hoverData: any = data != null && data.datasets != null && tooltipItem != null && tooltipItem.datasetIndex != null ? data.datasets[tooltipItem.datasetIndex] : [];
                            var tooltipLabel: any = data != null && data.labels != null && tooltipItem != null && tooltipItem.index != null ? data.labels[tooltipItem.index] : [];
                            var tooltipData: any = allData != null && allData.length > 0 && tooltipItem != null && tooltipItem.index != null ? allData[tooltipItem.index] : [];

                            return hoverData.label + ' ' + parseInt(tooltipData.y) + '%';

                        }
                    }
                },
                scales: {
                    xAxes: [{
                        maxBarThickness: 30,
                        barPercentage: 0.5,
                        categoryPercentage: 1.0,
                        scaleLabel: {
                            display: true,
                            labelString: 'Month',
                            fontSize: 15,
                            fontStyle: "bold",
                        },
                        type: 'time',
                        distribution: 'series',
                        bounds: 'ticks',
                        displayFormats: {
                            'month': 'MM',
                        },
                        gridLines: {
                            display: false
                        },
                        time: {
                            unit: 'month',
                            unitStepSize: 1,
                            max: this.maxTime,
                            min: this.minTime,
                            displayFormats: {
                                'month': 'MMM'
                            }
                        },
                    }],
                    yAxes: [
                        {
                            id: "y-axis-0",
                            position: 'left',
                            scaleLabel: {
                                display: true,
                                labelString: '% achieved',
                                fontSize: 15,
                                fontStyle: "bold",
                            },
                            gridLines: {
                                display: true
                            },
                            ticks: {
                                beginAtZero: true,
                            }
                        },
                    ]

                }
            }
        }

        return config;
    }

    createChart() {
        if (this.chart != null) this.chart.destroy();
        if (!this.basechart) return;

        let datasets = [] as any;

        if (this.chartData.availablePercentage) {
            datasets.push({
                type: 'bar',
                label: '% achieved',
                yAxisID: 'y-axis-0',
                data: this.chartData.availablePercentage,
                backgroundColor: '#00539f',
                borderColor: '#00539f',
                pointBorderColor: '#00539f',
                pointBackgroundColor: '#00539f',
                pointHoverBackgroundColor: '#00539f',
                fill: false
            },
            {
                label: 'Recommended benchmark',
                data: [],
                backgroundColor: '#93c01f',
                pointHoverBackgroundColor: '#93c01f',
                legend: {
                    display: true
                }
            },
            {
                label: 'Concern',
                data: [],
                backgroundColor: '#C82B2B',
                pointHoverBackgroundColor: '#C82B2B',
                legend: {
                    display: true
                }
            }
            );
        }


        let config = this.getChartConfig();
        let chartConfiguration: Chart.ChartConfiguration = {
            type: config.type,
            options: config.options,
            data: {
                datasets: datasets
            }
        };

        if (this.debounceId) {
            window.clearTimeout(this.debounceId);
        };

        this.debounceId = window.setTimeout(() => {
            if (this.chart != null) this.chart.destroy();
            let ctx = this.basechart.first.nativeElement;
            this.chart = new Chart(ctx, chartConfiguration);
        }, this.debounceTimeout);
    }

}
